import React, { useState } from "react";
import {
  ArrowRightIcon,
  EnvelopeIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import { API_BASE_URL, axiosInstance } from "@time-theft-analyzer/shared";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { resolvePostLoginUrl } from "../utils/returnUrl";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const [searchParams] = useSearchParams();
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleMicrosoftLogin = () => {
    // Forward the return URL through the SAML flow. The backend persists it in the
    // SAML session and, on callback success, redirects to `${FRONTEND_URL}${returnUrl}?token=`,
    // so the user lands back on the page they originally requested — no backend change needed.
    const returnUrl = resolvePostLoginUrl(searchParams.get("returnUrl"));
    window.location.href = `${API_BASE_URL}/auth/saml?returnUrl=${encodeURIComponent(returnUrl)}`;
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axiosInstance.post("/auth/api/login", {
        email,
        password,
      });

      if (response.data.success && response.data.token) {
        localStorage.setItem("token", response.data.token);
        // Sanitize before navigating to avoid open-redirect via a crafted ?returnUrl.
        const returnUrl = resolvePostLoginUrl(searchParams.get("returnUrl"));
        navigate(returnUrl);
      }
    } catch (err: any) {
      console.error("Login error:", err);
      setError(
        err.response?.data?.message ||
        t("auth.loginFailed")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel — Hero Branding */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700">
        {/* Decorative circles */}
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-white/5 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-white/5 blur-3xl" />
        <div className="absolute top-1/3 right-1/4 w-64 h-64 rounded-full bg-indigo-400/10 blur-2xl animate-float" />

        {/* Dot grid */}
        <div className="absolute top-0 right-0 w-full h-full opacity-[0.07]" style={{
          backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }} />

        {/* Geometric shapes */}
        <div className="absolute top-20 right-20 w-20 h-20 border-2 border-white/10 rounded-2xl rotate-12" />
        <div className="absolute bottom-32 left-16 w-14 h-14 border-2 border-white/10 rounded-xl -rotate-6" />
        <div className="absolute top-1/2 right-12 w-3 h-3 bg-white/20 rounded-full" />
        <div className="absolute top-32 left-1/3 w-2 h-2 bg-white/15 rounded-full" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Top — Logo */}
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20">
              <svg className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </div>
            <span className="text-white/90 font-semibold text-lg tracking-tight">Inzint</span>
          </div>

          {/* Center — Hero text */}
          <div className="max-w-lg">
            <h1 className="text-5xl font-bold text-white leading-[1.15] tracking-tight">
              Your workspace,{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-200 via-white to-violet-200">
                reimagined.
              </span>
            </h1>
            <p className="text-white/60 text-lg mt-5 leading-relaxed max-w-md">
              HRM, CRM, Finance, Projects — everything your team needs, unified in one elegant platform.
            </p>

            {/* Feature pills */}
            <div className="flex flex-wrap gap-2.5 mt-8">
              {["HRM", "CRM", "Finance", "Projects", "Documents", "Assets"].map((tag) => (
                <span
                  key={tag}
                  className="px-3.5 py-1.5 rounded-full text-xs font-medium text-white/80 bg-white/10 border border-white/10 backdrop-blur-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Bottom — Testimonial / stat */}
          <div className="flex items-center gap-4">
            <div className="flex -space-x-2">
              {["#6366F1", "#8B5CF6", "#EC4899", "#3B82F6"].map((color, i) => (
                <div
                  key={i}
                  className="h-8 w-8 rounded-full border-2 border-indigo-600 flex items-center justify-center text-[10px] font-bold text-white"
                  style={{ backgroundColor: color }}
                >
                  {["J", "A", "S", "R"][i]}
                </div>
              ))}
            </div>
            <div>
              <p className="text-white/80 text-sm font-medium">Trusted by teams everywhere</p>
              <p className="text-white/40 text-xs mt-0.5">Enterprise-grade security & compliance</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel — Login Form */}
      <div className="w-full lg:w-[45%] flex items-center justify-center bg-tk-base relative">
        {/* Subtle background mesh */}
        <div className="absolute inset-0 bg-tk-mesh" />

        {/* Mobile-only top accent */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-tk-accent via-violet-500 to-purple-500 lg:hidden" />

        <div className="w-full max-w-[400px] px-6 sm:px-8 relative z-10 animate-slide-up">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-10">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-gradient-to-br from-tk-accent to-tk-violet shadow-lg shadow-indigo-500/25 mb-4">
              <svg className="h-7 w-7 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-tk-text tracking-tight">Inzint App Suite</h1>
          </div>

          {/* Form header */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-tk-text tracking-tight">
              {showEmailLogin ? t("auth.signIn") : t("auth.welcomeBack")}
            </h2>
            <p className="text-sm text-tk-text-secondary mt-1.5">
              {showEmailLogin
                ? t("auth.enterCredentials")
                : t("auth.signInToContinue")}
            </p>
          </div>

          {!showEmailLogin ? (
            <div className="space-y-4">
              {/* Microsoft Login */}
              <button
                onClick={handleMicrosoftLogin}
                className="group w-full flex items-center gap-3 px-5 py-3.5 bg-white border border-tk-border rounded-xl text-sm font-medium text-tk-text hover:border-tk-border-accent hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-200"
              >
                <div className="h-8 w-8 rounded-lg bg-tk-base flex items-center justify-center flex-shrink-0 group-hover:bg-indigo-50 transition-colors duration-200">
                  <svg className="w-4 h-4" viewBox="0 0 23 23">
                    <path fill="#f35325" d="M1 1h10v10H1z" />
                    <path fill="#81bc06" d="M12 1h10v10H12z" />
                    <path fill="#05a6f0" d="M1 12h10v10H1z" />
                    <path fill="#ffba08" d="M12 12h10v10H12z" />
                  </svg>
                </div>
                <span className="flex-1 text-left">{t("auth.continueWithMicrosoft")}</span>
                <ArrowRightIcon className="h-4 w-4 text-tk-text-muted group-hover:text-tk-accent group-hover:translate-x-0.5 transition-all duration-200" />
              </button>

              {/* Divider */}
              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-tk-border" />
                </div>
                <div className="relative flex justify-center">
                  <span className="px-3 bg-tk-base text-[11px] font-semibold text-tk-text-muted uppercase tracking-widest">
                    {t("auth.or")}
                  </span>
                </div>
              </div>

              {/* Email Login */}
              <button
                onClick={() => setShowEmailLogin(true)}
                className="group w-full flex items-center gap-3 px-5 py-3.5 bg-white border border-tk-border rounded-xl text-sm font-medium text-tk-text hover:border-tk-border-accent hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-200"
              >
                <div className="h-8 w-8 rounded-lg bg-tk-base flex items-center justify-center flex-shrink-0 group-hover:bg-indigo-50 transition-colors duration-200">
                  <EnvelopeIcon className="h-4 w-4 text-tk-text-muted group-hover:text-tk-accent transition-colors duration-200" />
                </div>
                <span className="flex-1 text-left">{t("auth.continueWithEmail")}</span>
                <ArrowRightIcon className="h-4 w-4 text-tk-text-muted group-hover:text-tk-accent group-hover:translate-x-0.5 transition-all duration-200" />
              </button>
            </div>
          ) : (
            <form onSubmit={handleEmailLogin} className="space-y-5">
              {error && (
                <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl animate-scale-in flex items-start gap-2.5">
                  <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-red-500 text-xs font-bold">!</span>
                  </div>
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div>
                <label
                  htmlFor="email"
                  className="block text-[11px] font-semibold text-tk-text-muted uppercase tracking-widest mb-1.5"
                >
                  {t("auth.emailLabel")}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <EnvelopeIcon className="h-[18px] w-[18px] text-tk-text-muted" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    className="block w-full pl-11 pr-4 py-2.5 bg-white border border-tk-border rounded-xl text-sm text-tk-text placeholder-tk-text-muted focus:outline-none focus:ring-2 focus:ring-tk-accent/20 focus:border-tk-accent/40 disabled:bg-tk-base disabled:cursor-not-allowed transition-all duration-200"
                    placeholder={t("auth.emailPlaceholder")}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-[11px] font-semibold text-tk-text-muted uppercase tracking-widest mb-1.5"
                >
                  {t("auth.passwordLabel")}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <LockClosedIcon className="h-[18px] w-[18px] text-tk-text-muted" />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    className="block w-full pl-11 pr-11 py-2.5 bg-white border border-tk-border rounded-xl text-sm text-tk-text placeholder-tk-text-muted focus:outline-none focus:ring-2 focus:ring-tk-accent/20 focus:border-tk-accent/40 disabled:bg-tk-base disabled:cursor-not-allowed transition-all duration-200"
                    placeholder={t("auth.passwordPlaceholder")}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-tk-text-muted hover:text-tk-accent transition-colors duration-200"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-[18px] w-[18px]" />
                    ) : (
                      <EyeIcon className="h-[18px] w-[18px]" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-tk-accent to-tk-violet text-white rounded-xl text-sm font-medium shadow-md shadow-indigo-500/25 hover:shadow-lg hover:shadow-indigo-500/30 hover:brightness-110 hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-md"
              >
                {loading ? (
                  <>
                    <div className="relative h-4 w-4">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
                    </div>
                    {t("auth.signingIn")}
                  </>
                ) : (
                  <>
                    {t("auth.signIn")}
                    <ArrowRightIcon className="h-4 w-4" />
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowEmailLogin(false);
                  setError("");
                  setEmail("");
                  setPassword("");
                  setShowPassword(false);
                }}
                className="w-full text-center text-sm text-tk-text-muted hover:text-tk-accent transition-colors duration-200 pt-1"
              >
                &larr; {t("auth.allSignInOptions")}
              </button>
            </form>
          )}

          {/* Security note */}
          <div className="mt-8 pt-6 border-t border-tk-border/60">
            <div className="flex items-center justify-center gap-2 text-tk-text-muted">
              <LockClosedIcon className="h-3.5 w-3.5" />
              <p className="text-[11px] font-medium">
                Protected with enterprise-grade encryption
              </p>
            </div>
          </div>

          {/* Footer */}
          <p className="text-center text-[11px] text-tk-text-muted mt-4">
            &copy; {new Date().getFullYear()} Inzint App Suite
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
